/* SchemeCard.jsx — shared full-record renderer for a single
 * government scheme. Engaging design: region-colored identity bar,
 * ₹ key-figure highlight visible without expanding, Verify/Apply CTA,
 * plus the full verbatim record on expand: warning + bestFor +
 * supportTypes + keyFigures + documents + applyProcess +
 * officialGovernmentLinks + verification.
 *
 * Used by both /ownpath (start-screen schemes browser) and
 * /ownpath/preview/:idea_id (inline schemes for a kept idea).
 */
import React, { useState } from 'react';
import {
  AlertTriangle, ExternalLink, ChevronDown, ChevronUp, BadgeCheck, IndianRupee, CalendarClock,
} from 'lucide-react';
import { ChipList } from './preview-ui/PreviewPrimitives';

const REGION_VIS = {
  'Andhra Pradesh': {
    bar: 'from-amber-400 to-orange-500',
    head: 'from-amber-50 to-orange-50 border-amber-200',
    badge: 'bg-amber-200 text-amber-900',
    money: 'bg-amber-50 border-amber-200 text-amber-900',
    label: 'text-amber-800',
  },
  Telangana: {
    bar: 'from-emerald-400 to-teal-500',
    head: 'from-emerald-50 to-teal-50 border-emerald-200',
    badge: 'bg-emerald-200 text-emerald-900',
    money: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    label: 'text-emerald-800',
  },
  Central: {
    bar: 'from-indigo-400 to-blue-500',
    head: 'from-indigo-50 to-blue-50 border-indigo-200',
    badge: 'bg-indigo-200 text-indigo-900',
    money: 'bg-indigo-50 border-indigo-200 text-indigo-900',
    label: 'text-indigo-800',
  },
};
const regionVis = (r) => (/central|india/i.test(r || '') ? REGION_VIS.Central : (REGION_VIS[r] || REGION_VIS.Central));

export default function SchemeCard({ scheme: s, defaultOpen = false, testidPrefix = 'op-scheme' }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!s) return null;
  const vis = regionVis(s.region);
  const keyFigure = Array.isArray(s.keyFigures) ? s.keyFigures[0] : null;
  const primaryLink = s.officialGovernmentLinks?.[0]?.url;
  return (
    <div
      className="rounded-2xl border-2 border-gray-200 bg-white overflow-hidden hover:shadow-md transition-shadow"
      data-testid={`${testidPrefix}-${s.id}`}
    >
      <div className={`h-1.5 w-full bg-gradient-to-r ${vis.bar}`} />
      <div className={`bg-gradient-to-r ${vis.head} px-5 py-4 border-b`}>
        <div className="flex flex-wrap items-center gap-2 mb-1.5 text-[11px] font-bold">
          <span className={`${vis.badge} px-2 py-0.5 rounded-full uppercase tracking-widest`}>
            {s.emoji ? `${s.emoji} ` : ''}{s.region}
          </span>
          {s.type && <span className="bg-white/70 text-gray-700 px-2 py-0.5 rounded-full">{s.type}</span>}
          {s.status && <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">{s.status}</span>}
          {s.verified && (
            <span className="bg-teal-100 text-teal-800 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
              <BadgeCheck size={11} /> Verified
            </span>
          )}
        </div>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg md:text-xl font-extrabold text-gray-900">{s.title}</h3>
            {s.snapshot && <p className="text-sm text-gray-700 mt-1">{s.snapshot}</p>}
            {keyFigure && (
              <div
                className={`mt-2.5 inline-flex items-start gap-2 rounded-xl border px-3 py-2 ${vis.money}`}
                data-testid={`${testidPrefix}-${s.id}-keyfigure`}
              >
                <IndianRupee size={14} className="shrink-0 mt-0.5" />
                <p className="text-xs font-bold leading-snug">{keyFigure}</p>
              </div>
            )}
          </div>
          <div className="shrink-0 flex flex-col items-end gap-2">
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="px-3 py-1.5 rounded-full bg-white border border-gray-300 text-gray-800 text-xs font-bold hover:bg-gray-50 inline-flex items-center gap-1"
              data-testid={`${testidPrefix}-${s.id}-toggle`}
              aria-expanded={open}
            >
              {open ? <>Hide details <ChevronUp size={13} /></> : <>View details <ChevronDown size={13} /></>}
            </button>
            {primaryLink && (
              <a
                href={primaryLink}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 rounded-full bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold inline-flex items-center gap-1 transition-colors"
                data-testid={`${testidPrefix}-${s.id}-apply`}
              >
                {s.ctaLabel || 'Check official source'} <ExternalLink size={11} />
              </a>
            )}
          </div>
        </div>
      </div>
      {open && (
        <div className="p-5 space-y-3">
          {s.warning && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle size={16} className="text-amber-700 shrink-0 mt-0.5" />
                <p className="text-sm text-gray-800 leading-relaxed">{s.warning}</p>
              </div>
            </div>
          )}
          {s.validityNote && (
            <div className="rounded-xl bg-blue-50 border border-blue-200 p-3" data-testid={`${testidPrefix}-${s.id}-validity`}>
              <div className="flex items-start gap-2">
                <CalendarClock size={16} className="text-blue-700 shrink-0 mt-0.5" />
                <p className="text-sm text-gray-800 leading-relaxed">{s.validityNote}</p>
              </div>
            </div>
          )}
          {s.eligibility && (
            <div>
              <p className={`text-[10px] uppercase font-bold tracking-widest mb-1 ${vis.label}`}>Eligibility</p>
              <p className="text-sm text-gray-800 leading-relaxed">{s.eligibility}</p>
            </div>
          )}
          {s.availability && (
            <div>
              <p className={`text-[10px] uppercase font-bold tracking-widest mb-1 ${vis.label}`}>Availability (as checked)</p>
              <p className="text-sm text-gray-800 leading-relaxed">{s.availability}</p>
            </div>
          )}
          {s.bestFor?.length > 0 && (
            <div>
              <p className={`text-[10px] uppercase font-bold tracking-widest mb-1.5 ${vis.label}`}>Best for</p>
              <ChipList items={s.bestFor} tone="emerald" />
            </div>
          )}
          {s.supportTypes?.length > 0 && (
            <div>
              <p className={`text-[10px] uppercase font-bold tracking-widest mb-1.5 ${vis.label}`}>Support types</p>
              <ChipList items={s.supportTypes} tone="teal" />
            </div>
          )}
          {s.keyFigures?.length > 0 && (
            <div>
              <p className={`text-[10px] uppercase font-bold tracking-widest mb-1.5 ${vis.label}`}>Key figures (indicative)</p>
              <div className="grid sm:grid-cols-2 gap-2" data-testid={`${testidPrefix}-${s.id}-keyfigures`}>
                {s.keyFigures.map((k, j) => (
                  <div key={j} className={`flex items-start gap-2 rounded-xl border p-2.5 ${vis.money}`}>
                    <IndianRupee size={13} className="shrink-0 mt-0.5" />
                    <p className="text-xs font-semibold text-gray-900 leading-relaxed">{k}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {s.documents?.length > 0 && (
            <div>
              <p className={`text-[10px] uppercase font-bold tracking-widest mb-1.5 ${vis.label}`}>Documents you&apos;ll need</p>
              <ChipList items={s.documents} tone="amber" />
            </div>
          )}
          {s.applyProcess?.length > 0 && (
            <div>
              <p className={`text-[10px] uppercase font-bold tracking-widest mb-1.5 ${vis.label}`}>Apply process</p>
              <ol className="space-y-2 text-sm">
                {s.applyProcess.map((step, j) => (
                  <li key={j} className="flex gap-3">
                    <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-800 font-bold text-xs flex items-center justify-center shrink-0">{j + 1}</span>
                    <span className="text-gray-800 leading-relaxed pt-0.5">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
          {s.officialGovernmentLinks?.length > 0 && (
            <div>
              <p className={`text-[10px] uppercase font-bold tracking-widest mb-1.5 ${vis.label}`}>Official links</p>
              <div className="flex flex-col gap-1.5">
                {s.officialGovernmentLinks.map((lk, j) => (
                  <a
                    key={j}
                    href={lk.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 hover:text-emerald-900 hover:underline"
                    data-testid={`${testidPrefix}-${s.id}-link-${j}`}
                  >
                    <ExternalLink size={14} /> {lk.label}
                  </a>
                ))}
              </div>
            </div>
          )}
          {s.fitBusinessIdeas?.length > 0 && (
            <div>
              <p className={`text-[10px] uppercase font-bold tracking-widest mb-1.5 ${vis.label}`}>
                Fits these business ideas
              </p>
              <ChipList items={s.fitBusinessIdeas} tone="purple" />
            </div>
          )}
          {s.verification?.status && (
            <p className="text-[11px] text-gray-500 italic pt-1">
              Verification: {s.verification.status}
              {s.verification.source ? ` · ${s.verification.source}` : ''}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
